import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Edit,
  Image,
  Block,
  CheckCircleOutline,
  DeleteOutline,
} from '@mui/icons-material';
import { Alert, CircularProgress } from '@mui/material';
import { FormSubmitButton, GlobalButton, NavigateLinks } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  liveTrackingChildrenPageBreadcrumbs,
  DESTINATION_ICON_PAGE_NAME,
  DRIVER_ICON_PAGE_NAME,
} from '../../config';
import { TrackingIconSlot } from '../models';
import {
  TrackingIconFormMode,
  TrackingIconFormValues,
  trackingIconPath,
} from '../models/TrackingIconForm';
import { useGetTrackingIconByIdQuery } from '../services/tracking-icons.service';
import { useTrackingIconActions } from '../hooks';
import TrackingIconForm from '../components/TrackingIconForm';

const titles: Record<TrackingIconFormMode, string> = {
  create: 'Crear ícono',
  edit: 'Editar ícono',
  image: 'Reemplazar imagen',
  view: 'Información del ícono',
};

const TrackingIconPage = ({
  slot,
  mode,
}: {
  slot: TrackingIconSlot;
  mode: TrackingIconFormMode;
}) => {
  const { iconId = '' } = useParams();
  const navigate = useNavigate();
  const listPath = trackingIconPath(slot);
  const [enableError, setEnableError] = useState<string>();
  const {
    currentData: icon,
    isFetching,
    error,
    refetch,
  } = useGetTrackingIconByIdQuery(iconId, {
    skip: mode === 'create' || !iconId,
  });
  const {
    onCreateTrackingIcon,
    onUpdateTrackingIcon,
    onReplaceTrackingIconImage,
    onDisableTrackingIcon,
    onDeleteTrackingIcon,
    onEnableTrackingIcon,
    isEnablingTrackingIcon,
    isDeletingTrackingIcon,
    isSavingTrackingIcon,
  } = useTrackingIconActions();
  const breadcrumbs = useMemo(
    () => [
      ...liveTrackingChildrenPageBreadcrumbs(navigate),
      {
        label:
          slot === 'destination'
            ? DESTINATION_ICON_PAGE_NAME
            : DRIVER_ICON_PAGE_NAME,
        onClick: () => navigate(listPath),
      },
    ],
    [navigate, slot, listPath]
  );
  const goBack = () => navigate(listPath);

  const onSubmit = async (data: TrackingIconFormValues) => {
    if (mode === 'create') {
      return onCreateTrackingIcon(slot, data, goBack);
    } else if (mode === 'image') {
      return onReplaceTrackingIconImage(iconId, data, goBack);
    } else if (mode === 'edit') {
      return onUpdateTrackingIcon(iconId, data, goBack);
    }
  };

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <NavigateLinks actualPage={titles[mode]} breadcrumbs={breadcrumbs} />
      {mode !== 'create' && isFetching && !icon ? (
        <p>Cargando...</p>
      ) : mode !== 'create' && (error || !icon || icon.slot !== slot) ? (
        <div className='flex flex-col gap-5' role='alert'>
          <p>No se pudo cargar el ícono solicitado.</p>
          {error && (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => {
                void refetch();
              }}
            >
              Reintentar
            </GlobalButton>
          )}
        </div>
      ) : (
        <>
          {mode === 'view' && (
            <div className='flex flex-wrap justify-end gap-[14px]'>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                disabled={isSavingTrackingIcon}
                action={() => navigate(`${listPath}/${iconId}/edit`)}
              >
                <Edit />
                Editar datos
              </GlobalButton>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                disabled={isSavingTrackingIcon}
                action={() => navigate(`${listPath}/${iconId}/image`)}
              >
                <Image />
                Reemplazar imagen
              </GlobalButton>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                disabled={isSavingTrackingIcon || isFetching}
                action={async () => {
                  setEnableError(undefined);
                  if (icon?.enabled) {
                    await onDisableTrackingIcon(iconId, goBack);
                  } else {
                    const message = await onEnableTrackingIcon(iconId);
                    if (message) setEnableError(message);
                  }
                }}
              >
                {isEnablingTrackingIcon ? (
                  <CircularProgress size={20} color='inherit' />
                ) : icon?.enabled ? (
                  <Block />
                ) : (
                  <CheckCircleOutline />
                )}
                {isEnablingTrackingIcon
                  ? 'Habilitando...'
                  : icon?.enabled
                    ? 'Deshabilitar'
                    : 'Habilitar'}
              </GlobalButton>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                disabled={isSavingTrackingIcon || isFetching || !icon}
                action={async () => {
                  if (!icon) return;
                  await onDeleteTrackingIcon(icon, goBack);
                }}
              >
                {isDeletingTrackingIcon ? (
                  <CircularProgress size={20} color='inherit' />
                ) : (
                  <DeleteOutline />
                )}
                {isDeletingTrackingIcon ? 'Eliminando...' : 'Eliminar ícono'}
              </GlobalButton>
            </div>
          )}
          {mode === 'view' && enableError && (
            <Alert severity='error'>{enableError}</Alert>
          )}
          <TrackingIconForm
            key={`${mode}-${iconId}`}
            slot={slot}
            icon={mode === 'create' ? undefined : icon}
            mode={mode}
            title={titles[mode]}
            isSaving={isSavingTrackingIcon}
            action={onSubmit}
            renderButtons={(submit, formState) => (
              <>
                <GlobalButton
                  variant={GlobalButtonVariant.secondaryButton}
                  action={goBack}
                  disabled={isSavingTrackingIcon || formState.isSubmitting}
                >
                  Cancelar
                </GlobalButton>
                <FormSubmitButton
                  action={submit}
                  isLoading={isSavingTrackingIcon || formState.isSubmitting}
                  disabled={
                    (mode === 'edit' && !formState.isDirty) ||
                    !!formState.errors.isDefault ||
                    !!formState.errors.dateFrom ||
                    !!formState.errors.dateTo
                  }
                  label={
                    isSavingTrackingIcon
                      ? 'Guardando...'
                      : mode === 'create'
                        ? 'Crear ícono'
                        : 'Guardar cambios'
                  }
                />
              </>
            )}
          />
        </>
      )}
    </section>
  );
};

export default TrackingIconPage;
